import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import EkgLine from "@/components/EkgLine";
import Navbar from "@/components/Navbar";
import NinjaBadge from "@/components/NinjaBadge";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success("Your ninja journey begins.");
      nav("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />
      <div className="relative min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
        <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-white/10 bg-ekg-grid">
          <div className="font-mono text-xs text-green-400 tracking-widest">
            NEW NINJA · INITIATION
          </div>
          <div className="flex justify-center">
            <NinjaBadge level={1} size={200} glow />
          </div>
          <div className="relative h-20">
            <EkgLine className="w-full h-full" color="#ef4444" />
          </div>
          <div className="max-w-md">
            <div className="font-display text-3xl font-semibold leading-tight">
              Your training starts with a single wave.
            </div>
            <p className="mt-3 text-sm text-slate-400">
              Eight belts stand between you and Cardiology Kage. Ready?
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 lg:p-12">
          <form
            onSubmit={submit}
            className="w-full max-w-sm space-y-5"
            data-testid="register-form"
          >
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-green-400 mb-2">
                Create Account
              </div>
              <h1 className="font-display text-3xl font-bold">Join the dojo</h1>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Ninja name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white/5 border-white/10 focus:border-green-500/60 h-11"
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/10 focus:border-green-500/60 h-11"
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password (min 6)</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 focus:border-green-500/60 h-11"
                data-testid="register-password-input"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2"
                data-testid="register-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold"
              data-testid="register-submit-btn"
            >
              {busy ? "Enlisting…" : "Begin training"}
            </Button>
            <div className="text-center text-sm text-slate-400">
              Already a ninja?{" "}
              <Link
                to="/login"
                className="text-green-400 hover:text-green-300 underline underline-offset-4"
                data-testid="register-to-login-link"
              >
                Log in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
